import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Shield, Database, Eye, Trash2, Lock } from "lucide-react";

const FleetyGoPrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="fleetygo-privacy">
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">Privacy Policy</h1>
      </header>

      <main className="flex-1 px-4 py-6 overflow-y-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">FleetyGo – Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-6">Effective Date: 25-12-2025</p>

        <p className="text-gray-600 text-sm mb-6">
          This Privacy Policy explains how FleetyGo ("we", "us", "our") collects, uses, shares, and protects your personal information when you use our package delivery service. By using FleetyGo, you consent to the practices described in this policy.
        </p>

        {/* Section 1 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Database size={20} className="text-[#3FA9F5]" />
            <h2 className="text-lg font-semibold text-gray-900">1. Information We Collect</h2>
          </div>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">1.1 Personal Information</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Name and phone number</li>
            <li>Email address (if provided)</li>
            <li>Pickup and delivery addresses</li>
            <li>Sender and receiver contact details</li>
          </ul>

          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">1.2 Transaction Information</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Order history and delivery details</li>
            <li>Payment information (processed securely via payment gateways)</li>
            <li>Package descriptions and weight category</li>
          </ul>

          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">1.3 Location Data</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>GPS location for pickup and delivery addresses</li>
            <li>Real-time location during active deliveries (for tracking purposes)</li>
            <li>Location data is used only during active orders</li>
          </ul>

          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">1.4 Device Information</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Device type, operating system, and app version</li>
            <li>Unique device identifiers</li>
            <li>IP address and browser information</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Eye size={20} className="text-[#3FA9F5]" />
            <h2 className="text-lg font-semibold text-gray-900">2. How We Use Your Information</h2>
          </div>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>To facilitate package pickup and delivery services</li>
            <li>To process payments and send transaction receipts</li>
            <li>To enable real-time order tracking</li>
            <li>To communicate order updates via SMS or push notifications</li>
            <li>To verify pickup and delivery through PIN verification</li>
            <li>To improve our services and user experience</li>
            <li>To detect and prevent fraud or abuse</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">3. Information Sharing</h2>
          <p className="text-gray-600 text-sm mb-2">We share your information only as necessary:</p>
          
          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">3.1 With Delivery Partners</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Pickup and delivery addresses</li>
            <li>Sender and receiver names and phone numbers</li>
            <li>Package details for proper handling</li>
          </ul>

          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">3.2 With Third-Party Service Providers</h3>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li><strong>Payment Gateways (Cashfree):</strong> To process secure payments</li>
            <li><strong>Google Maps:</strong> For location services and route calculation</li>
            <li><strong>SMS/Notification Services:</strong> To send order updates</li>
          </ul>

          <h3 className="text-sm font-medium text-gray-800 mt-3 mb-2">3.3 Legal Requirements</h3>
          <p className="text-gray-600 text-sm">
            We may disclose information when required by law, court order, or government request.
          </p>
        </section>

        {/* Section 4 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={20} className="text-[#3FA9F5]" />
            <h2 className="text-lg font-semibold text-gray-900">4. Data Security</h2>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-green-800 font-medium">🔐 Your Data is Protected</p>
            <p className="text-sm text-green-700 mt-1">
              We implement industry-standard security measures to protect your information.
            </p>
          </div>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Encryption of data in transit (HTTPS/SSL)</li>
            <li>Secure storage of personal information</li>
            <li>PIN-based verification for pickup and delivery</li>
            <li>Regular security audits and monitoring</li>
            <li>Access controls limiting employee access to data</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Data Retention</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Order history is retained for 2 years for service and legal purposes</li>
            <li>Location data from completed orders is retained for 90 days</li>
            <li>Account information is retained until account deletion</li>
            <li>Payment records are retained as per legal requirements</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={20} className="text-[#3FA9F5]" />
            <h2 className="text-lg font-semibold text-gray-900">6. Your Rights</h2>
          </div>
          <p className="text-gray-600 text-sm mb-2">You have the right to:</p>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Opt-out:</strong> Unsubscribe from promotional communications</li>
            <li><strong>Data Portability:</strong> Request your data in a portable format</li>
          </ul>
          <p className="text-gray-600 text-sm mt-2">
            To exercise these rights, contact us at <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5]">support@fleety.co.in</a>
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Trash2 size={20} className="text-[#3FA9F5]" />
            <h2 className="text-lg font-semibold text-gray-900">7. Account Deletion</h2>
          </div>
          <p className="text-gray-600 text-sm mb-2">To delete your FleetyGo account:</p>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>Email us at <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5]">support@fleety.co.in</a> with subject "Account Deletion Request"</li>
            <li>Include your registered phone number for verification</li>
            <li>Deletion will be processed within 30 days</li>
            <li>Some data may be retained for legal compliance</li>
          </ul>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-3">
            <p className="text-sm text-orange-800 font-medium">⚠️ Note</p>
            <p className="text-sm text-orange-700 mt-1">
              Account deletion is permanent. All order history, saved addresses, and preferences will be deleted.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Cookies & Tracking</h2>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            <li>We use cookies for authentication and session management</li>
            <li>Analytics cookies help us improve app performance</li>
            <li>You can manage cookie preferences in your device settings</li>
          </ul>
        </section>

        {/* Section 9 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Children's Privacy</h2>
          <p className="text-gray-600 text-sm">
            FleetyGo is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
          </p>
        </section>

        {/* Section 10 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Third-Party Links</h2>
          <p className="text-gray-600 text-sm">
            Our app may contain links to third-party websites or services. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Changes to This Policy</h2>
          <p className="text-gray-600 text-sm">
            We may update this Privacy Policy from time to time. Significant changes will be notified through the app or via email. Continued use of FleetyGo after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">12. Governing Law</h2>
          <p className="text-gray-600 text-sm">
            This Privacy Policy is governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Bengaluru, Karnataka.
          </p>
        </section>

        {/* Section 13 */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">13. Contact Us</h2>
          <p className="text-gray-600 text-sm mb-3">For privacy-related questions or concerns:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Mail size={16} className="text-[#3FA9F5]" />
              <a href="mailto:support@fleety.co.in" className="text-[#3FA9F5]">support@fleety.co.in</a>
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Phone size={16} className="text-[#3FA9F5]" />
              <a href="tel:+919845331817" className="text-[#3FA9F5]">+91-98453 31817</a>
            </div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <MapPin size={16} className="text-[#3FA9F5]" />
              <span>Bengaluru, India</span>
            </div>
          </div>
        </section>

        <p className="text-center text-xs text-gray-400 mt-8">© 2025 Fleety. All rights reserved.</p>
      </main>
    </div>
  );
};

export default FleetyGoPrivacyPage;
